/*
 * Authors: mariushirjanu
 * Created: 22/12/2023
 */
import _ from 'underscore';
import { util } from '../../util';
import moment from 'moment';
import { Injectable } from '@angular/core';
import { TranslationService } from '../../translation/translation.service';
import { ColDef, GridOptions, IRowNode, ValueFormatterFunc, ValueFormatterParams } from 'ag-grid-community';
import { IObject } from '../../../../type_definitions/datatypes';
import { IAsset } from '../../../main/order/asset/asset-data.service';
import { CSS_ACTIONS_COLUMN, IFormGridColDef } from '../../form-grid/form-grid.component';
import { GaMessageService } from '../../message/ga-message.service';
import { GaAgGridService } from '../ga-ag-grid.service';

export interface IAgCellValidator {
  min?:number;
  minLength?:number;
  maxLength?:number;
  minDate?:Date;
  maxDate?:Date;
  pattern?:string;
  required?:boolean;
}

export interface IAgCellValidatorParam {
  colDef:ColDef;
  context:IObject;
  data:IObject;
  value:any;
}

export interface IAgCellValidatorOption {
  validateRequired?:boolean; // apply validation for required fields
  validateRequiredUndefined?:boolean; // validation of required fields even if the value its value is undefined
}

@Injectable()
export class AgCellValidatorService {
  constructor(
    private translationService:TranslationService,
    private messageService:GaMessageService,
    private gaAgGridService:GaAgGridService
  ){
    /***/
  }

  public validateCell(params:IAgCellValidatorParam, options?:IAgCellValidatorOption):string[] {
    const errorMessages:string[] = [];

    const field:string = params?.colDef?.field;
    const headerName:string = params?.colDef?.headerName;
    const validateConfig:IAgCellValidator = params?.context?.validator[field]?.validateConfigFn && params.context.validator[field].validateConfigFn(params); // call validator function (defined on context) with the cell data (loaded from the server)
    const cellEditorParams:IAgCellValidator = params?.colDef?.cellEditorParams; // use the parameters from column definition

    // apply formatter to the error message
    const valueFormatterFn:Function = (valueFormatterParams:ValueFormatterParams) => {
      return (params?.colDef?.valueFormatter && (<ValueFormatterFunc>params?.colDef?.valueFormatter)(valueFormatterParams)) || params?.value;
    };

    // apply validation for required
    const required:boolean = (validateConfig?.required || cellEditorParams?.required) && (options?.validateRequired || util.isNullOrUndefined(options?.validateRequired));
    if(required && (params?.value === '' || params?.value === null || (!params?.value && options?.validateRequiredUndefined))) { // params?.value === null -> for input type number
      errorMessages.push(this.translationService.instant('GA.VALIDATION.REQUIRED', {
        label: headerName
      }));
    }

    // apply validation for minimum length
    const minLength:number = _.isNumber(validateConfig?.minLength) ? validateConfig?.minLength : cellEditorParams?.minLength;
    if (minLength && params?.value && params.value.length < minLength) {
      errorMessages.push(
        this.translationService.instant('GA.VALIDATION.MINLENGTH', {
          label: headerName,
          minlength: minLength
        })
      );
    }

    // apply validation for maximum length
    const maxLength:number = _.isNumber(validateConfig?.maxLength) ? validateConfig?.maxLength : cellEditorParams?.maxLength;
    if (maxLength && params?.value && params.value.length > maxLength) {
      errorMessages.push(
        this.translationService.instant('GA.VALIDATION.MAXLENGTH', {
          label: headerName,
          maxlength: maxLength
        })
      );
    }


    // apply validation for the minimum date
    const minDate:Date = validateConfig?.minDate || cellEditorParams?.minDate;
    if(minDate && params?.value && params.value < minDate){
      errorMessages.push(
        this.translationService.instant('GA.VALIDATION.MINDATE', {
          label: headerName,
          mindate: valueFormatterFn({ value: moment(minDate).add(1, 'day') })
        })
      );
    }

    // apply validation for the maximum date
    const maxDate:Date = validateConfig?.maxDate || cellEditorParams?.maxDate;
    if(maxDate && params?.value && params.value > maxDate){
      errorMessages.push(
        this.translationService.instant('GA.VALIDATION.MAXDATE', {
          label: headerName,
          maxdate: valueFormatterFn({ value: maxDate })
        })
      );
    }

    // apply pattern match validation
    const pattern:string = validateConfig?.pattern || cellEditorParams?.pattern;
    const regex:RegExp = new RegExp(validateConfig?.pattern || cellEditorParams?.pattern);
    if(pattern && params?.value && !regex.test(params.value)){
      errorMessages.push(
        this.translationService.instant('GA.VALIDATION.INVALID', {
          label: valueFormatterFn(params.value)
        })
      );
    }

    // apply validation for min value for numeric inputs
    const min:number = validateConfig?.min || cellEditorParams?.min;
    if(util.notNullOrUndefined(min) && util.notNullOrUndefinedOrEmpty(params?.value) && params.value < min){
      errorMessages.push(
        this.translationService.instant('GA.VALIDATION.MIN', {
          label: headerName,
          min
        })
      );
    }

    return errorMessages;
  }

  public validateEditableCells(colDefs:IFormGridColDef[], gridOptions:GridOptions, gridId:string, showErrorDialog?:boolean):Promise<string[] | void > {
    let errors:string[] = [];
    for (let idx:number = 0; idx < this.gaAgGridService.getDisplayedRowCount(gridId); idx++) {
      const rowNode:IRowNode = this.gaAgGridService.getDisplayedRowAtIndex(gridId, idx);
      const data:IAsset = _.clone(rowNode.data);

      _.each(colDefs, (colDef:IFormGridColDef) => {
        let errorMessages:string[] = [];
        if(colDef.editable === true && colDef.cellClass !== CSS_ACTIONS_COLUMN){
          errorMessages = this.validateCell({
            colDef,
            context: gridOptions.context,
            data,
            value: data[colDef.field]
          });

          // collect uniq error messages
          errors = _.uniq(errors.concat(errorMessages));
        }
      });
    }

    // If there are local errors means we reject with showing error dialog with list of error messages
    if (errors.length) {
      if(showErrorDialog) {
        this.messageService.showErrorDialog(this.translationService.instant('GA.VALIDATION.VALIDATION_FAILED'), `
        <ul>
          ${_.map(errors, (error:string) => {
          return `<li>${error}</li>`;
        }).join('')}
        </ul>
      `);
      }

      return Promise.reject(errors);
    }

    return Promise.resolve();
  }
}